/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormGroup,
	InputLabel,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	SelectChangeEvent,
	Step,
	StepLabel,
	Stepper,
	TextField,
	Typography,
} from '@mui/material';
import {
	IBankAccountInfo,
	IPassenger,
	IRefundItem,
	IRefundPaymentInfo,
	IRefundRequest,
	IServices,
	OperationalRequestCategoryEnum,
	RefundBankAccountType,
	RefundReasonEnum,
	RefundRequestTypeEnum,
	RefundScopeEnum,
	TypeServiceEnum,
} from 'features/salesOrder/types';
import { flightAncillaryOptions, REFUND_TYPE_META } from 'features/salesOrder/constants';
import { ServiceSelector } from './ServiceSelector';
import { FileUploader } from './FileUploader';
import { createOperationalRequest } from 'features/salesOrder/services';
import { extractErrorMessage } from 'features/quotation/helpers';
import { ShowAlertState } from 'features/common/types';
import { fileToBase64Attachment } from '../../helpers';

interface RefundRequestModalProps {
	open: boolean;
	onClose: () => void;
	services: IServices[];
	passengers: IPassenger[];
	saleOrderId: string;
	onSuccess: (caseId: number) => void;
	onSetAlert: (alert: ShowAlertState) => void;
}

export const refundTypes: {
	value: RefundRequestTypeEnum;
	label: string;
	description: string;
}[] = (Object.entries(REFUND_TYPE_META) as Array<[RefundRequestTypeEnum, { label: string; description: string }]>).map(
	([value, meta]) => ({
		value,
		label: meta.label,
		description: meta.description,
	}),
);

const currencies = [
	{ value: 'USD', label: 'USD' },
	{ value: 'UYU', label: 'UYU' },
	{ value: 'PYG', label: 'PYG' },
	{ value: 'EUR', label: 'EUR' },
	{ value: 'COP', label: 'COP' },
	{ value: 'BOB', label: 'BOB' },
	{ value: 'BRL', label: 'BRL' },
];

const accountTypes = [
	{ value: RefundBankAccountType.SavingsAccount, label: 'Caja de ahorros' },
	{ value: RefundBankAccountType.CheckingAccount, label: 'Cuenta corriente' },
];

const refundReasons = [
	{ value: RefundReasonEnum.VoluntaryTaxesOnly, label: 'Voluntario solo tasas' },
	{ value: RefundReasonEnum.VoluntaryWithPenalty, label: 'Voluntario con penalidad' },
	{ value: RefundReasonEnum.ReschedulingNotAccepted, label: 'Reprogramación NO aceptada' },
	{ value: RefundReasonEnum.IllnessOrDeath, label: 'Enfermedad o fallecimiento' },
];

// Los steps cambian según el tipo de devolución
const getSteps = (refundType: RefundRequestTypeEnum) => {
	if (refundType === RefundRequestTypeEnum.Immediate) {
		return ['Tipo de devolución', 'Selección de servicios', 'Información de pago', 'Revisión'];
	}
	return ['Tipo de devolución', 'Selección de servicios', 'Información adicional', 'Revisión'];
};

export const RefundRequestModal: React.FC<RefundRequestModalProps> = ({
	open,
	onClose,
	services,
	passengers,
	saleOrderId,
	onSuccess,
	onSetAlert,
}) => {
	const [activeStep, setActiveStep] = useState(0);
	const [refundType, setRefundType] = useState<RefundRequestTypeEnum>(RefundRequestTypeEnum.Voluntary);
	const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
	const [refundItems, setRefundItems] = useState<IRefundItem[]>([]);
	const [paymentAmount, setPaymentAmount] = useState('');
	const [paymentCurrency, setPaymentCurrency] = useState('USD');
	const [supportingFiles, setSupportingFiles] = useState<File[]>([]);
	const [bankAccount, setBankAccount] = useState<IBankAccountInfo>({
		bank: '',
		holder: '',
		accountType: '',
		currency: '',
		accountNumber: '',
	});
	const [additionalComments, setAdditionalComments] = useState('');
	const [supportingDocuments, setSupportingDocuments] = useState<File[]>([]);
	const [loading, setLoading] = useState(false);

	// Extraer tickets disponibles por servicio (no mezclados) para Flight y FlightAncillary
	const getTicketsByService = (serviceId: string) => {
		const service = services.find((s) => s.keyId === serviceId);
		const tickets: Array<{ ticket: string; passengerId: string; passengerName: string }> = [];

		if (
			service &&
			(service.serviceType === TypeServiceEnum.Flight || service.serviceType === TypeServiceEnum.FlightAncillary)
		) {
			if (service.serviceType === TypeServiceEnum.Flight) {
				const groups = (service as any).passengersGroupByPassengerType || [];
				groups.forEach((group: any) => {
					if (group.passengersTickets && Array.isArray(group.passengersTickets)) {
						group.passengersTickets.forEach((ticketData: any) => {
							if (ticketData.ticket && ticketData.passengerId) {
								const passenger = passengers.find((p) => p.keyId === ticketData.passengerId);
								const passengerName = passenger ? `${passenger.name} ${passenger.lastName}` : 'Pasajero no encontrado';
								tickets.push({
									ticket: ticketData.ticket,
									passengerId: ticketData.passengerId,
									passengerName,
								});
							}
						});
					}
				});
			} else if (service.serviceType === TypeServiceEnum.FlightAncillary) {
				const ticketsData = (service as any).passengersTickets || [];
				ticketsData.forEach((ticketData: any) => {
					if (ticketData.ticket && ticketData.passengerId) {
						const passenger = passengers.find((p) => p.keyId === ticketData.passengerId);
						const passengerName = passenger ? `${passenger.name} ${passenger.lastName}` : 'Pasajero no encontrado';
						tickets.push({
							ticket: ticketData.ticket,
							passengerId: ticketData.passengerId,
							passengerName,
						});
					}
				});
			}
		}
		return tickets;
	};

	// Función helper para obtener fecha de inicio del servicio
	const getServiceStartDate = (service: IServices): string | null => {
		if (!service) return null;

		// Para vuelos, buscar en flightLegs
		if (service.serviceType === TypeServiceEnum.Flight) {
			const flightLegs = (service as any).flightLegs || [];
			if (flightLegs.length > 0 && flightLegs[0].departure?.date) {
				return flightLegs[0].departure.date;
			}
		}

		// Para traslados, buscar en transferSegments
		if (service.serviceType === TypeServiceEnum.Transfer) {
			const transferSegments = (service as any).transferSegments || [];
			if (transferSegments.length > 0 && transferSegments[0].startDate) {
				return transferSegments[0].startDate;
			}
		}

		// Para otros servicios, buscar startDate, useDate, etc.
		if ((service as any).startDate) {
			return (service as any).startDate;
		}
		if ((service as any).useDate) {
			return (service as any).useDate;
		}

		return null;
	};

	// Función helper para formatear fecha
	const formatDate = (dateString: string | null): string => {
		if (!dateString) return 'No disponible';
		try {
			if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
				const [year, month, day] = dateString.split('-');
				return `${day}/${month}/${year}`;
			}
			const date = new Date(dateString);
			const day = date.getDate().toString().padStart(2, '0');
			const month = (date.getMonth() + 1).toString().padStart(2, '0');
			const year = date.getFullYear();
			return `${day}/${month}/${year}`;
		} catch {
			return 'No disponible';
		}
	};

	// Función helper para verificar si hay vuelos con motivo "enfermedad o fallecimiento"
	const hasFlightsWithIllnessOrDeathReason = (): boolean => {
		return refundItems.some(
			(item) =>
				(item.serviceType === TypeServiceEnum.Flight || item.serviceType === TypeServiceEnum.FlightAncillary) &&
				item.refundReason === RefundReasonEnum.IllnessOrDeath,
		);
	};

	// Sincronizar refundItems con selectedServiceIds cuando cambian los servicios seleccionados
	useEffect(() => {
		if (activeStep === 1 && selectedServiceIds.length > 0) {
			setRefundItems((prevItems) => {
				// Crear un mapa de los refundItems existentes para preservar la configuración
				const existingItemsMap = new Map(prevItems.map((item) => [item.serviceId, item]));

				// Crear nuevos items para los servicios seleccionados
				const newItems = selectedServiceIds.map((serviceId) => {
					// Si ya existe un item para este servicio, mantenerlo
					const existingItem = existingItemsMap.get(serviceId);
					if (existingItem) {
						return existingItem;
					}

					// Si no existe, crear uno nuevo con scope por defecto
					const service = services.find((s) => s.keyId === serviceId);
					return {
						serviceId,
						serviceType: service?.serviceType || TypeServiceEnum.Other,
						scope: RefundScopeEnum.FullService,
						refundReason: undefined,
					};
				});

				// Solo actualizar si hay cambios en los IDs
				const prevIds = prevItems
					.map((item) => item.serviceId)
					.sort()
					.join(',');
				const newIds = newItems
					.map((item) => item.serviceId)
					.sort()
					.join(',');

				if (prevIds !== newIds) {
					return newItems;
				}
				return prevItems;
			});
		} else if (activeStep === 1 && selectedServiceIds.length === 0) {
			// Limpiar refundItems si no hay servicios seleccionados
			setRefundItems([]);
		}
	}, [selectedServiceIds, activeStep, services]);

	const handleRefundTypeChange = (event: SelectChangeEvent) => {
		setRefundType(event.target.value as RefundRequestTypeEnum);
	};

	const handleNext = () => {
		if (activeStep === 0) {
			// Validar tipo de devolución seleccionado
			if (!refundType) {
				onSetAlert({
					show: true,
					severity: 'error',
					message: 'Debe seleccionar un tipo de devolución',
				});
				return;
			}
		}

		if (activeStep === 1) {
			// Validar servicios seleccionados
			if (selectedServiceIds.length === 0) {
				onSetAlert({
					show: true,
					severity: 'error',
					message: 'Debe seleccionar al menos un servicio',
				});
				return;
			}

			// Validar motivo de devolución para vuelos en devolución voluntaria
			if (refundType === RefundRequestTypeEnum.Voluntary) {
				for (const item of refundItems) {
					const service = services.find((s) => s.keyId === item.serviceId);
					if (
						(service?.serviceType === TypeServiceEnum.Flight ||
							service?.serviceType === TypeServiceEnum.FlightAncillary) &&
						!item.refundReason
					) {
						onSetAlert({
							show: true,
							severity: 'error',
							message: 'Debe seleccionar un motivo de devolución para todos los servicios de vuelo',
						});
						return;
					}
				}
			}

			// refundItems ya está sincronizado por el useEffect, no es necesario inicializarlo aquí
		}

		if (activeStep === 2) {
			// Validar según el tipo de devolución
			if (refundType === RefundRequestTypeEnum.Immediate) {
				// Validación para devolución inmediata (como errores internos)
				if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
					onSetAlert({
						show: true,
						severity: 'error',
						message: 'Debe ingresar un monto válido',
					});
					return;
				}

				if (
					!bankAccount.bank ||
					!bankAccount.holder ||
					!bankAccount.accountType ||
					!bankAccount.currency ||
					!bankAccount.accountNumber
				) {
					onSetAlert({
						show: true,
						severity: 'error',
						message: 'Debe completar todos los datos bancarios',
					});
					return;
				}
			} else if (refundType === RefundRequestTypeEnum.Voluntary) {
				// Para devolución voluntaria, validar si hay vuelos con motivo "enfermedad o fallecimiento"
				if (hasFlightsWithIllnessOrDeathReason()) {
					if (supportingDocuments.length === 0) {
						onSetAlert({
							show: true,
							severity: 'error',
							message:
								'Debe subir al menos un archivo. Es obligatorio adjuntar la carta de exoneración de penalidad por parte de la compañía aérea.',
						});
						return;
					}
				}
			}
		}

		setActiveStep((prevStep) => prevStep + 1);
	};

	const handleBack = () => {
		setActiveStep((prevStep) => prevStep - 1);
	};

	const handleRefundItemScopeChange = (index: number, scope: RefundScopeEnum) => {
		const newItems = [...refundItems];
		newItems[index].scope = scope;
		if (scope === RefundScopeEnum.FullService) {
			newItems[index].specificItems = undefined;
		}
		setRefundItems(newItems);
	};

	const handleTicketToggle = (serviceId: string, ticket: string) => {
		const newItems = refundItems.map((item) => {
			if (item.serviceId === serviceId) {
				const currentTickets = item.specificItems || [];
				if (currentTickets.includes(ticket)) {
					return {
						...item,
						specificItems: currentTickets.filter((t) => t !== ticket),
					};
				} else {
					return {
						...item,
						specificItems: [...currentTickets, ticket],
					};
				}
			}
			return item;
		});
		setRefundItems(newItems);
	};

	const handleCommentsChange = (serviceId: string, comments: string) => {
		const newItems = refundItems.map((item) => {
			if (item.serviceId === serviceId) {
				return {
					...item,
					additionalComments: comments || undefined,
				};
			}
			return item;
		});
		setRefundItems(newItems);
	};

	const handleRefundReasonChange = (serviceId: string, reason: string) => {
		const newItems = refundItems.map((item) => {
			if (item.serviceId === serviceId) {
				return {
					...item,
					refundReason: reason || undefined,
				};
			}
			return item;
		});
		setRefundItems(newItems);
	};

	const handleSubmit = async () => {
		try {
			setLoading(true);

			const baseRequest: IRefundRequest = {
				requestCategory: OperationalRequestCategoryEnum.Refunds,
				requestType: refundType,
				saleOrderId,
				serviceIds: selectedServiceIds,
				refundItems,
			};

			if (refundType === RefundRequestTypeEnum.Immediate) {
				baseRequest.paymentInfo = {
					refundAmountToCustomer: parseFloat(paymentAmount),
					currency: paymentCurrency,
					bankAccount,
				} as IRefundPaymentInfo;
				if (supportingFiles.length > 0) {
					const paymentInfoFilesToAttachments = await Promise.all(supportingFiles.map(fileToBase64Attachment));
					baseRequest.paymentInfo.files = paymentInfoFilesToAttachments;
				}
				baseRequest.additionalComments = undefined;
			} else if (refundType === RefundRequestTypeEnum.Voluntary) {
				if (additionalComments) {
					baseRequest.additionalComments = additionalComments;
				}
				if (supportingDocuments.length > 0) {
					const supportingDocumentsToAttachments = await Promise.all(supportingDocuments.map(fileToBase64Attachment));
					baseRequest.supportingDocuments = supportingDocumentsToAttachments;
				}
			}

			const response = await createOperationalRequest(baseRequest);
			onSuccess(response.data.caseId);
			onSetAlert({
				show: true,
				severity: 'success',
				message: `Solicitud de devolución creada exitosamente. Caso #${response.data.caseId} creado en el CRM.`,
			});

			// Reset form
			setActiveStep(0);
			setRefundType(RefundRequestTypeEnum.Voluntary);
			setSelectedServiceIds([]);
			setRefundItems([]);
			setPaymentAmount('');
			setPaymentCurrency('USD');
			setSupportingFiles([]);
			setBankAccount({
				bank: '',
				holder: '',
				accountType: '',
				currency: '',
				accountNumber: '',
			});
			setAdditionalComments('');
			setSupportingDocuments([]);
			onClose();
		} catch (error) {
			onSetAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al crear la solicitud de devolución'),
			});
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		if (!loading) {
			setActiveStep(0);
			setRefundType(RefundRequestTypeEnum.Voluntary);
			setSelectedServiceIds([]);
			setRefundItems([]);
			setPaymentAmount('');
			setPaymentCurrency('USD');
			setSupportingFiles([]);
			setBankAccount({
				bank: '',
				holder: '',
				accountType: '',
				currency: '',
				accountNumber: '',
			});
			setAdditionalComments('');
			setSupportingDocuments([]);
			onClose();
		}
	};

	const renderStepContent = () => {
		switch (activeStep) {
			case 0: {
				const selectedType = refundTypes.find((t) => t.value === refundType);
				return (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
						<FormControl fullWidth>
							<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
								Tipo de devolución *
							</Typography>
							<Select value={refundType} onChange={handleRefundTypeChange} disabled={loading}>
								{refundTypes.map((type) => (
									<MenuItem key={type.value} value={type.value}>
										{type.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						{selectedType && selectedType.description && (
							<Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
								<Typography variant="body2">{selectedType.description}</Typography>
							</Box>
						)}
					</Box>
				);
			}

			case 1: {
				// Función helper para obtener el nombre del tipo de servicio
				const getServiceTypeLabel = (serviceType: TypeServiceEnum): string => {
					const typeLabels: Partial<Record<TypeServiceEnum, string>> = {
						[TypeServiceEnum.Flight]: 'Vuelo',
						[TypeServiceEnum.FlightAncillary]: 'Adicional de vuelo',
						[TypeServiceEnum.FlightAddOn]: 'Adicional de vuelo',
						[TypeServiceEnum.Accommodation]: 'Alojamiento',
						[TypeServiceEnum.CarRental]: 'Alquiler de auto',
						[TypeServiceEnum.Transfer]: 'Traslado',
						[TypeServiceEnum.Activity]: 'Actividad',
						[TypeServiceEnum.Insurance]: 'Seguro',
						[TypeServiceEnum.Circuit]: 'Circuito',
						[TypeServiceEnum.Train]: 'Tren',
						[TypeServiceEnum.Bus]: 'Bus',
						[TypeServiceEnum.Ferry]: 'Ferry',
						[TypeServiceEnum.Cruise]: 'Crucero',
						[TypeServiceEnum.ThirdPartyPackage]: 'Paquete terceros',
						[TypeServiceEnum.Fee]: 'Tarifa',
						[TypeServiceEnum.Other]: 'Otro',
					};
					return typeLabels[serviceType] || 'Desconocido';
				};

				// Función helper para obtener el nombre del proveedor
				const getProviderName = (service: IServices | undefined): string | null => {
					if (!service) return null;
					const tariffProvider = (service as any).tariffProvider;
					if (!tariffProvider) return null;

					if (typeof tariffProvider === 'string') {
						return tariffProvider;
					}

					if (typeof tariffProvider === 'object' && tariffProvider.fantasyName) {
						return tariffProvider.fantasyName;
					}

					return null;
				};

				// Función helper para obtener el subtipo de servicio "Otro"
				const getOtherServiceSubtype = (service: IServices | undefined): string | null => {
					if (!service || service.serviceType !== TypeServiceEnum.Other) return null;
					const otherServiceType = (service as any).otherServiceType;
					return otherServiceType || null;
				};

				// Función helper para obtener el subtipo de "Adicional de vuelo"
				const getFlightAncillarySubtype = (service: IServices | undefined): string | null => {
					if (!service || service.serviceType !== TypeServiceEnum.FlightAncillary) return null;
					const flightAncillaryType = (service as any).flightAncillaryType;
					if (!flightAncillaryType) return null;

					const option = flightAncillaryOptions.find((opt) => opt.id === flightAncillaryType);
					return option ? option.name : flightAncillaryType;
				};

				// Función helper para obtener el prestador del servicio de alojamiento
				const getAccommodationServiceProvider = (service: IServices | undefined): string | null => {
					if (!service || service.serviceType !== TypeServiceEnum.Accommodation) return null;
					const serviceProvider = (service as any).serviceProvider;
					if (!serviceProvider || serviceProvider === 'n/a') return null;

					if (typeof serviceProvider === 'object' && serviceProvider.name) {
						return serviceProvider.name;
					}

					return null;
				};

				return (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
						<ServiceSelector
							services={services}
							selectedServiceIds={selectedServiceIds}
							onChange={setSelectedServiceIds}
							disabled={loading}
						/>

						{selectedServiceIds.length > 0 && (
							<Box>
								<Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
									Configurar alcance de devolución por servicio
								</Typography>
								{refundItems.map((item, index) => {
									const service = services.find((s) => s.keyId === item.serviceId);
									const serviceTickets = getTicketsByService(item.serviceId);
									const serviceStartDate = service ? getServiceStartDate(service) : null;

									return (
										<Box
											key={item.serviceId}
											sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
										>
											<Box sx={{ mb: 2 }}>
												<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
													{service ? (service as any).reference || `Servicio ${index + 1}` : `Servicio ${index + 1}`}
												</Typography>
												<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
													Tipo: {getServiceTypeLabel(service?.serviceType || TypeServiceEnum.Other)}
												</Typography>
												{/* Mostrar proveedor en todos los casos */}
												{getProviderName(service) && (
													<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
														Proveedor: {getProviderName(service)}
													</Typography>
												)}
												{/* Mostrar subtipo para servicios "Otro" */}
												{getOtherServiceSubtype(service) && (
													<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
														Subtipo: {getOtherServiceSubtype(service)}
													</Typography>
												)}
												{/* Mostrar subtipo para "Adicional de vuelo" */}
												{getFlightAncillarySubtype(service) && (
													<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
														Subtipo: {getFlightAncillarySubtype(service)}
													</Typography>
												)}
												{/* Mostrar prestador del servicio para Alojamiento */}
												{getAccommodationServiceProvider(service) && (
													<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
														Prestador del servicio: {getAccommodationServiceProvider(service)}
													</Typography>
												)}
												{serviceStartDate && (
													<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
														Fecha de inicio: {formatDate(serviceStartDate)}
													</Typography>
												)}
											</Box>
											{/* Select de motivo de devolución para vuelos en devolución voluntaria */}
											{refundType === RefundRequestTypeEnum.Voluntary &&
												(service?.serviceType === TypeServiceEnum.Flight ||
													service?.serviceType === TypeServiceEnum.FlightAncillary) && (
													<Box sx={{ mb: 2 }}>
														<FormControl fullWidth>
															<InputLabel>Motivo de devolución *</InputLabel>
															<Select
																value={item.refundReason || ''}
																onChange={(e) => handleRefundReasonChange(item.serviceId, e.target.value)}
																label="Motivo de devolución *"
																disabled={loading}
															>
																{refundReasons.map((reason) => (
																	<MenuItem key={reason.value} value={reason.value}>
																		{reason.label}
																	</MenuItem>
																))}
															</Select>
														</FormControl>
													</Box>
												)}
											<Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
												<FormControl component="fieldset" sx={{ flex: 1 }}>
													<RadioGroup
														value={item.scope}
														onChange={(e) => handleRefundItemScopeChange(index, e.target.value as RefundScopeEnum)}
													>
														{(service?.serviceType === TypeServiceEnum.Flight ||
															service?.serviceType === TypeServiceEnum.FlightAncillary) && (
															<>
																<FormControlLabel
																	value={RefundScopeEnum.FullService}
																	control={<Radio />}
																	label="Totalidad del servicio"
																/>
																<FormControlLabel
																	value={RefundScopeEnum.SpecificTickets}
																	control={<Radio />}
																	label="eTickets específicos"
																/>
																{item.scope === RefundScopeEnum.SpecificTickets && serviceTickets.length > 0 && (
																	<Box sx={{ ml: 4, mt: 1 }}>
																		<FormGroup>
																			{serviceTickets.map((ticketInfo) => (
																				<FormControlLabel
																					key={ticketInfo.ticket}
																					control={
																						<Checkbox
																							checked={(item.specificItems || []).includes(ticketInfo.ticket)}
																							onChange={() => handleTicketToggle(item.serviceId, ticketInfo.ticket)}
																						/>
																					}
																					label={
																						<Box>
																							<Typography variant="body2" component="span" sx={{ fontWeight: 'bold' }}>
																								{ticketInfo.ticket}
																							</Typography>
																							<Typography
																								variant="body2"
																								component="span"
																								sx={{ ml: 1, color: 'text.secondary' }}
																							>
																								- {ticketInfo.passengerName}
																							</Typography>
																						</Box>
																					}
																				/>
																			))}
																		</FormGroup>
																	</Box>
																)}
															</>
														)}
														{service?.serviceType !== TypeServiceEnum.Flight &&
															service?.serviceType !== TypeServiceEnum.FlightAncillary && (
																<FormControlLabel
																	value={RefundScopeEnum.FullService}
																	control={<Radio />}
																	label="Totalidad del servicio"
																/>
															)}
													</RadioGroup>
												</FormControl>
												<TextField
													label="Comentarios adicionales"
													multiline
													rows={2}
													value={item.additionalComments || ''}
													onChange={(e) => handleCommentsChange(item.serviceId, e.target.value)}
													disabled={loading}
													sx={{ flex: 1, minWidth: '200px' }}
													placeholder="Ingrese comentarios adicionales sobre este servicio (opcional)"
												/>
											</Box>
										</Box>
									);
								})}
							</Box>
						)}
					</Box>
				);
			}

			case 2: {
				const requiresMandatoryFile =
					refundType === RefundRequestTypeEnum.Voluntary && hasFlightsWithIllnessOrDeathReason();

				return (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
						{refundType === RefundRequestTypeEnum.Voluntary && (
							<>
								{requiresMandatoryFile && (
									<Alert severity="warning" sx={{ mb: 2 }}>
										<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
											Documento obligatorio requerido
										</Typography>
										<Typography variant="body2">
											Debido a que esta solicitud contiene tickets aéreos con motivo &quot;enfermedad o
											fallecimiento&quot; es mandatorio la carga de una carta de &quot;exoneración de penalidad&quot;
											por parte de la compañía aérea. La misma debe ser en formato PDF, en donde se indique que el
											pasajero no podrá cumplir con el itinerario como está originalmente programado (incapacidad,
											controles médicos, etc.) y se observen claramente la afectación para la fecha del vuelo y el
											respectivo sello médico, membrete de la entidad clínica y firma del médico.
										</Typography>
									</Alert>
								)}
								<TextField
									label="Comentarios adicionales generales"
									multiline
									rows={3}
									value={additionalComments}
									onChange={(e) => setAdditionalComments(e.target.value)}
									disabled={loading}
									fullWidth
								/>
								<FileUploader
									files={supportingDocuments}
									onChange={setSupportingDocuments}
									accept=".pdf,.jpg,.jpeg,.png"
									maxFiles={5}
									maxSizeMB={10}
									disabled={loading}
									label={
										requiresMandatoryFile
											? 'Documentos de soporte (certificado médico, partida de fallecimiento, etc.) *'
											: 'Documentos de soporte (certificado médico, partida de fallecimiento, etc.)'
									}
								/>
							</>
						)}

						{/* Información de pago solo para devolución inmediata */}
						{refundType === RefundRequestTypeEnum.Immediate && (
							<>
								<FileUploader
									files={supportingFiles}
									onChange={setSupportingFiles}
									accept=".pdf,.jpg,.jpeg,.png"
									maxFiles={5}
									maxSizeMB={10}
									disabled={loading}
									label="Adjuntar archivos"
								/>

								<Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
									<TextField
										label="Monto a rembolsar al cliente"
										type="number"
										value={paymentAmount}
										onChange={(e) => setPaymentAmount(e.target.value)}
										disabled={loading}
										sx={{ flex: 1 }}
										required
									/>
									<FormControl sx={{ minWidth: 120 }} required>
										<InputLabel>Moneda</InputLabel>
										<Select
											value={paymentCurrency}
											onChange={(e) => setPaymentCurrency(e.target.value)}
											disabled={loading}
											label="Moneda"
										>
											{currencies.map((currency) => (
												<MenuItem key={currency.value} value={currency.value}>
													{currency.label}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Box>

								<Typography variant="subtitle2" sx={{ mt: 2, fontWeight: 'bold' }}>
									Cuenta bancaria para realizar el reembolso *
								</Typography>
								<TextField
									label="Banco"
									value={bankAccount.bank}
									onChange={(e) => setBankAccount({ ...bankAccount, bank: e.target.value })}
									disabled={loading}
									fullWidth
									required
								/>
								<TextField
									label="Titular"
									value={bankAccount.holder}
									onChange={(e) => setBankAccount({ ...bankAccount, holder: e.target.value })}
									disabled={loading}
									fullWidth
									required
								/>
								<FormControl fullWidth required>
									<InputLabel>Tipo de cuenta</InputLabel>
									<Select
										value={bankAccount.accountType}
										onChange={(e) => setBankAccount({ ...bankAccount, accountType: e.target.value })}
										disabled={loading}
										label="Tipo de cuenta"
									>
										{accountTypes.map((type) => (
											<MenuItem key={type.value} value={type.value}>
												{type.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<FormControl fullWidth required>
									<InputLabel>Moneda</InputLabel>
									<Select
										value={bankAccount.currency}
										onChange={(e) => setBankAccount({ ...bankAccount, currency: e.target.value })}
										disabled={loading}
										label="Moneda"
									>
										{currencies.map((currency) => (
											<MenuItem key={currency.value} value={currency.value}>
												{currency.label}
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<TextField
									label="Número de cuenta"
									value={bankAccount.accountNumber}
									onChange={(e) => setBankAccount({ ...bankAccount, accountNumber: e.target.value })}
									disabled={loading}
									fullWidth
									required
								/>
							</>
						)}
					</Box>
				);
			}

			case 3: {
				return (
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						<Typography variant="h6">Resumen de la solicitud</Typography>
						<Box>
							<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
								Tipo de devolución:
							</Typography>
							<Typography variant="body2">{refundTypes.find((t) => t.value === refundType)?.label}</Typography>
						</Box>
						<Box>
							<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
								Servicios seleccionados:
							</Typography>
							{refundItems.map((item) => {
								const service = services.find((s) => s.keyId === item.serviceId);
								const serviceRef = service ? (service as any).reference || item.serviceId : item.serviceId;
								return (
									<Box key={item.serviceId} sx={{ mb: 1 }}>
										<Typography variant="body2">• {serviceRef}</Typography>
										{refundType === RefundRequestTypeEnum.Voluntary &&
											(item.serviceType === TypeServiceEnum.Flight ||
												item.serviceType === TypeServiceEnum.FlightAncillary) &&
											item.refundReason && (
												<Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
													Motivo: {refundReasons.find((r) => r.value === item.refundReason)?.label || item.refundReason}
												</Typography>
											)}
									</Box>
								);
							})}
						</Box>
						{refundType === RefundRequestTypeEnum.Voluntary && (
							<>
								{additionalComments && (
									<Box>
										<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
											Comentarios adicionales:
										</Typography>
										<Typography variant="body2">{additionalComments}</Typography>
									</Box>
								)}
								{supportingDocuments.length > 0 && (
									<Box>
										<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
											Documentos de soporte:
										</Typography>
										<Typography variant="body2">{supportingDocuments.length} archivo(s)</Typography>
									</Box>
								)}
							</>
						)}
						{refundType === RefundRequestTypeEnum.Immediate && (
							<>
								<Box>
									<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
										Monto a rembolsar:
									</Typography>
									<Typography variant="body2">
										{paymentAmount} {paymentCurrency}
									</Typography>
								</Box>
								{supportingFiles.length > 0 && (
									<Box>
										<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
											Archivos adjuntos:
										</Typography>
										<Typography variant="body2">{supportingFiles.length} archivo(s)</Typography>
									</Box>
								)}
								<Box>
									<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
										Cuenta bancaria:
									</Typography>
									<Typography variant="body2">Banco: {bankAccount.bank}</Typography>
									<Typography variant="body2">Titular: {bankAccount.holder}</Typography>
									<Typography variant="body2">
										Tipo de cuenta:{' '}
										{accountTypes.find((t) => t.value === bankAccount.accountType)?.label || bankAccount.accountType}
									</Typography>
									<Typography variant="body2">Moneda: {bankAccount.currency}</Typography>
									<Typography variant="body2">Número de cuenta: {bankAccount.accountNumber}</Typography>
								</Box>
							</>
						)}
					</Box>
				);
			}

			default:
				return null;
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
			<DialogTitle>Solicitud de devolución</DialogTitle>
			<DialogContent>
				<Box sx={{ mt: 2, mb: 3 }}>
					<Stepper activeStep={activeStep}>
						{getSteps(refundType).map((label) => (
							<Step key={label}>
								<StepLabel>{label}</StepLabel>
							</Step>
						))}
					</Stepper>
				</Box>
				<Box sx={{ minHeight: '400px' }}>{renderStepContent()}</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancelar
				</Button>
				{activeStep > 0 && (
					<Button onClick={handleBack} disabled={loading}>
						Atrás
					</Button>
				)}
				{activeStep < getSteps(refundType).length - 1 ? (
					<Button onClick={handleNext} variant="contained" disabled={loading}>
						Siguiente
					</Button>
				) : (
					<Button
						onClick={handleSubmit}
						variant="contained"
						disabled={loading}
						startIcon={loading && <CircularProgress size={16} />}
					>
						Crear solicitud
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};
